import { getAuthRoles, getLoggedInUserGroupMembers, getUserInfo, login } from 'API/auth'
import { AuthenticateParams } from 'configs/constants/enums'
import Errors from 'configs/errors'
import { AuthRole } from 'interfaces/auth'
import { IOrganization } from 'interfaces/organization'
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { AuthUserProfile, ILoginRequest } from 'types/auth'
import { isValidEmail } from 'utils/common'
import { getSubdomain } from 'utils/domain'
import { fcmTokenManager } from 'services/fcmTokenManager.service'

export default class AuthStore {
  rootStore: RootStore

  accessToken: string = ''
  fcmToken: string = ''
  resetPasswordToken?: string
  userDetail: AuthUserProfile = {} as AuthUserProfile
  authRoles: AuthRole[] = []
  isDisabled: boolean = false
  isShowSurveyPopup: boolean = true

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public async getMyUser(): Promise<void> {
    const accessToken: string = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN) || ''
    if (accessToken) {
      this.accessToken = accessToken
      try {
        const userInfo = await getUserInfo()
        const groupMembers = await getLoggedInUserGroupMembers()
        this.rootStore.groupStore.setGroupMembers(groupMembers)
        this.userDetail = userInfo
        this.rootStore.userStore.setCurrentUserGroupMembers(groupMembers)
        this.rootStore.userStore.setCurrentUser(userInfo)
      } catch (error: any) {
        if (error.response.status === 401) {
          // expired ACCESS TOKEN -> force logout
          alert('Login session expired!')
          this.logout()
          return
        }
        throw error
      }
    }
  }

  public setAccessToken(accessToken: string): void {
    this.accessToken = accessToken
    localStorage.setItem(AuthenticateParams.ACCESS_TOKEN, accessToken)
  }

  public setFCMToken(token: string): void {
    this.fcmToken = token
  }

  public async login(usernameOrEmail: string, password: string, organization?: IOrganization): Promise<boolean> {
    const currentOrg = this.rootStore.organizationStore.organization ?? organization
    if (isEmpty(currentOrg)) {
      return false
    }

    try {
      const loginData: ILoginRequest = { password, organizationId: currentOrg?.id ?? 0 }
      const isEmail = isValidEmail(trim(usernameOrEmail))
      if (isEmail === true) {
        loginData.email = trim(usernameOrEmail)
      } else {
        loginData.username = trim(usernameOrEmail)
      }
      const loginResp = await login(loginData)

      this.isDisabled = loginResp?.userInfo?.disabled ?? false
      if (loginResp?.userInfo?.disabled) {
        return false
      }

      if (loginResp?.userInfo.isResetPassword) {
        this.resetPasswordToken = loginResp?.userInfo.resetPasswordToken
        return false
      }

      this.accessToken = loginResp?.token
      localStorage.setItem(AuthenticateParams.ACCESS_TOKEN, loginResp?.token)
      const groupMembers = await getLoggedInUserGroupMembers()

      this.rootStore.groupStore.setGroupMembers(groupMembers)

      this.rootStore.userStore.setCurrentUserGroupMembers(groupMembers)
      this.rootStore.userStore.setCurrentUser(loginResp?.userInfo)

      const storageOrg = Number(localStorage.getItem(AuthenticateParams.ORG_ID))

      if (
        loginResp?.userInfo?.organizationId !== currentOrg?.id &&
        loginResp?.userInfo?.organizationId !== storageOrg
      ) {
        throw new Error()
      }

      this.userDetail = loginResp?.userInfo
      return true
    } catch (e: any) {
      localStorage.removeItem(AuthenticateParams.ACCESS_TOKEN)
      const serverMessage = e?.response?.data?.error?.message
      throw new Error(serverMessage || Errors.LOGIN_FAILED)
    }
  }

  public logout(): void {
    // Clear FCM token and Firebase messaging
    fcmTokenManager.clearToken()
    
    localStorage.removeItem(AuthenticateParams.ACCESS_TOKEN)
    const subdomain = getSubdomain()
    const mainhost = window.location.host.replace(/^(www\.)/, '').replace(new RegExp(`^(${subdomain}\\.)`), '')
    window.location.href = `${window.location.protocol}//app.${mainhost}`
    this.resetPasswordToken = undefined
  }

  public async fetchauthRoles(): Promise<void> {
    this.authRoles = await getAuthRoles()
  }

  public setShowSurveyPopup(value: boolean): void {
    this.isShowSurveyPopup = value
  }
}
